import {
  Bell,
  CalendarClock,
  Check,
  ChevronDown,
  ExternalLink,
  FileText,
  Mail,
  MessageSquareText,
  MousePointerClick,
  Plus,
  Search,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ActionBehavior, MessageSection } from "../../../shared/types";
import { api } from "../../api";
import { IconButton, newId } from "../../components/primitives";
import {
  commonTimezones,
  timezoneIsSupported,
  timezoneOffsetLabel,
  timezonePlace,
  timezoneSearchText,
  timezoneShortName,
  timezones,
  zonedInputToIso,
  zonedInputValue,
} from "../../composer/timezones";
import { SlackField } from "./RichTextEditor";
import { RsvpDateTimePicker } from "./RsvpDateTimePicker";

type ButtonAction = Extract<MessageSection, { type: "actions" }>["actions"][number];
type EditorBehavior =
  | "open_url"
  | "open_form"
  | "show_ephemeral"
  | "notify_sender"
  | "dm_clicker"
  | "rsvp"
  | "join_user_group";
type FormField = NonNullable<ButtonAction["formFields"]>[number];

const actionChoices = [
  {
    value: "open_url",
    title: "Open a link",
    description: "Send them to a URL",
    icon: ExternalLink,
  },
  {
    value: "open_form",
    title: "Open a form",
    description: "Collect one or more answers",
    icon: FileText,
  },
  {
    value: "show_ephemeral",
    title: "Private message",
    description: "Only the clicker sees it",
    icon: MessageSquareText,
  },
  {
    value: "notify_sender",
    title: "Message me",
    description: "Send you a DM",
    icon: Bell,
  },
  {
    value: "dm_clicker",
    title: "DM the clicker",
    description: "Send them a direct message",
    icon: Mail,
  },
  {
    value: "rsvp",
    title: "RSVP to an event",
    description: "Confirm, localize time, and remind",
    icon: CalendarClock,
  },
  {
    value: "join_user_group",
    title: "Join user group",
    description: "Add the clicker to a Slack user group",
    icon: UserPlus,
  },
] as const;

const templateVariables = [
  ["Person's name", "{{clicker_name}}"],
  ["@person", "{{clicker_mention}}"],
  ["Your name", "{{sender_name}}"],
  ["@you", "{{sender_mention}}"],
  ["#channel", "{{channel}}"],
  ["Button", "{{button_label}}"],
] as const;

const eventTemplateVariables = [
  ["Event", "{{event_title}}"],
  ["Time (configured)", "{{event_time}}"],
  ["Recipient’s local time", "{{event_time_local}}"],
  ["Original event time", "{{event_time_original}}"],
  ["Slack local time", "{{event_slack_time}}"],
  ["Event timezone", "{{event_timezone}}"],
  ["Their timezone", "{{clicker_timezone}}"],
] as const;

const userGroupTemplateVariables = [
  ["Group", "@{{group_name}}"],
  ["Member count", "{{member_count}}"],
] as const;

function timezoneOffsetAt(date: Date, timezone: string) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    })
      .formatToParts(date)
      .map((part) => [part.type, part.value]),
  );
  return Math.round(
    (Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour),
      Number(parts.minute),
      Number(parts.second),
    ) -
      date.getTime()) /
      60_000,
  );
}

export function timezoneEventSummary(startsAt: string, timezone: string) {
  const date = new Date(startsAt);
  if (!Number.isFinite(date.getTime())) return "Choose a valid event time.";
  try {
    const day = new Intl.DateTimeFormat(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
      timeZone: timezone,
    }).format(date);
    const time24 = new Intl.DateTimeFormat(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
      timeZone: timezone,
      timeZoneName: "short",
    }).format(date);
    const time12 = new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      minute: "2-digit",
      hourCycle: "h12",
      timeZone: timezone,
    }).format(date);
    return `${day}, ${time24} (${time12})`;
  } catch {
    return date.toISOString();
  }
}

function daylightSavingWarning(startsAt: string, timezone: string) {
  const event = new Date(startsAt);
  if (!Number.isFinite(event.getTime())) return undefined;
  try {
    const eventOffset = timezoneOffsetAt(event, timezone);
    const before = timezoneOffsetAt(new Date(event.getTime() - 7 * 24 * 60 * 60_000), timezone);
    const after = timezoneOffsetAt(new Date(event.getTime() + 7 * 24 * 60 * 60_000), timezone);
    if (before !== after)
      return "A daylight-saving clock change happens near this date. Double-check the displayed local time before sending.";
    if (timezoneOffsetAt(new Date(), timezone) !== eventOffset)
      return "This date uses a different UTC offset than today because of summer/standard time. bChannel keeps the exact event instant.";
  } catch {
    return "This timezone could not be checked for daylight-saving changes.";
  }
  return undefined;
}

function defaultRsvp(timezone: string): NonNullable<ButtonAction["rsvp"]> {
  const startsAt = new Date();
  startsAt.setSeconds(0, 0);
  return {
    eventTitle: "M*A*S*H movie night",
    eventStartsAt: startsAt.toISOString(),
    eventTimezone: timezone,
    timeDisplay: "local_and_original",
    timeFormat: "24",
    showEventDetails: false,
    showEventTitle: true,
    showEventTime: true,
    showAttendees: false,
    showCountdown: false,
    messageTimeDisplay: "local_and_original",
    sendEphemeral: true,
    ephemeralText: ":yay: *You’re in for {{event_title}}*\n{{event_time}}\nYour RSVP is saved.",
    dmClicker: true,
    dmText: ":yay: *{{event_title}}*\n{{event_time}}\nYou’re confirmed. Any enabled reminders will arrive here.",
    notifyYou: false,
    notifyYouText: ":ms-inbox: {{clicker_mention}} RSVP’d to *{{event_title}}*\n{{event_time_original}}",
    reminders: [
      {
        offsetMinutes: 15,
        enabled: true,
        message: ":hii: *{{event_title}} starts in 15 minutes*\n{{event_time}}",
      },
      {
        offsetMinutes: 5,
        enabled: false,
        message: ":hii: *{{event_title}} starts in 5 minutes*\n{{event_time}}",
      },
      {
        offsetMinutes: 0,
        enabled: true,
        message: ":woooo: *{{event_title}} is starting now*\n{{event_time}}",
      },
    ],
  };
}

function editorBehavior(behavior: ActionBehavior): EditorBehavior {
  if (behavior === "record" || behavior === "add_to_list") return "show_ephemeral";
  if (behavior === "collect_input") return "open_form";
  return behavior;
}

function formFieldsFor(action: ButtonAction): FormField[] {
  if (action.formFields?.length) return action.formFields;
  return [
    {
      id: "answer",
      label: action.label || "Your answer",
      type: "long_text",
      placeholder: action.prompt ?? "Write your answer",
      required: true,
    },
  ];
}

function isChoiceField(type: FormField["type"]) {
  return type === "single_select" || type === "radio" || type === "checkboxes";
}

function behaviorPatch(action: ButtonAction, behavior: EditorBehavior, timezone: string): Partial<ButtonAction> {
  if (behavior === "open_form") {
    return {
      behavior,
      formTitle: action.formTitle ?? "Quick form",
      formSubmitLabel: action.formSubmitLabel ?? "Submit",
      formFields: formFieldsFor(action),
    };
  }
  if (behavior === "show_ephemeral") {
    return { behavior, responseText: action.responseText ?? "Thanks — your response was recorded." };
  }
  if (behavior === "notify_sender") {
    return {
      behavior,
      notificationText: action.notificationText ?? "{{clicker_mention}} clicked “{{button_label}}” in {{channel}}.",
      responseText: action.responseText ?? "Your message was sent.",
    };
  }
  if (behavior === "dm_clicker") {
    return {
      behavior,
      dmText: action.dmText ?? "Hi {{clicker_name}} — thanks for responding.",
      responseText: action.responseText ?? "Check your DMs.",
    };
  }
  if (behavior === "rsvp")
    return {
      behavior,
      label: ["learn more", "new button"].includes(action.label.trim().toLowerCase()) ? "RSVP" : action.label,
      style: action.style === "default" ? "primary" : action.style,
      rsvp: action.rsvp ?? defaultRsvp(timezone),
    };
  if (behavior === "join_user_group")
    return {
      behavior,
      label: ["learn more", "new button"].includes(action.label.trim().toLowerCase())
        ? "Join user group"
        : action.label,
      style: action.style === "default" ? "primary" : action.style,
      userGroup: action.userGroup,
      privateReply: action.privateReply ?? true,
      dmClicker: action.dmClicker ?? false,
      responseText: action.responseText ?? "You’re in for @{{group_name}}. :yay:",
      notificationText:
        action.notificationText ??
        "{{clicker_mention}} joined the *@{{group_name}}* user group ({{member_count}} members).",
    };
  return { behavior, url: action.url ?? "https://example.com" };
}

export function TemplateTextarea({
  id,
  label,
  help,
  value,
  placeholder,
  includeEventVariables = false,
  extraVariables = [],
  onChange,
}: {
  id: string;
  label: string;
  help: string;
  value: string;
  placeholder: string;
  includeEventVariables?: boolean;
  extraVariables?: ReadonlyArray<readonly [string, string]>;
  onChange: (value: string) => void;
}) {
  const insertVariable = (token: string) => onChange(`${value}${value && !/\s$/.test(value) ? " " : ""}${token}`);
  return (
    <div className="action-field action-field-wide template-field">
      <label htmlFor={id}>{label}</label>
      <SlackField
        mode="mrkdwn"
        id={id}
        value={value}
        maxLength={1_000}
        onChange={onChange}
        multiline
        ariaLabel={label}
        placeholder={placeholder}
      />
      <div className="template-help">
        <span>{help}</span>
        <fieldset className="template-tokens">
          <legend className="visually-hidden">Insert message variable</legend>
          {[...templateVariables, ...(includeEventVariables ? eventTemplateVariables : []), ...extraVariables].map(
            ([name, token]) => (
              <button key={token} type="button" title={`Insert ${token}`} onClick={() => insertVariable(token)}>
                {name}
              </button>
            ),
          )}
        </fieldset>
      </div>
    </div>
  );
}

function TimezonePicker({
  value,
  slackTimezone,
  eventStartsAt,
  onChange,
}: {
  value: string;
  slackTimezone: string;
  eventStartsAt: string;
  onChange: (timezone: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const searchInput = useRef<HTMLInputElement>(null);
  const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const eventDate = new Date(eventStartsAt);
  const displayDate = Number.isFinite(eventDate.getTime()) ? eventDate : new Date();
  const zones = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    const pinned = [value, slackTimezone, localTimezone, "UTC", ...Object.keys(commonTimezones)];
    const ordered = [...new Set([...pinned, ...timezones])].filter(timezoneIsSupported);
    if (!normalized) return ordered;
    const terms = normalized.split(/\s+/).filter(Boolean);
    return ordered
      .filter((timezone) => terms.every((term) => timezoneSearchText(timezone).includes(term)))
      .sort((left, right) => {
        const leftExact = timezoneSearchText(left).startsWith(normalized) ? 0 : 1;
        const rightExact = timezoneSearchText(right).startsWith(normalized) ? 0 : 1;
        return leftExact - rightExact || timezonePlace(left).localeCompare(timezonePlace(right));
      });
  }, [localTimezone, query, slackTimezone, value]);
  useEffect(() => {
    if (open) searchInput.current?.focus();
  }, [open]);
  const choose = (timezone: string) => {
    onChange(timezone);
    setOpen(false);
    setQuery("");
  };
  return (
    <div className="timezone-picker">
      <span className="timezone-picker-label">Event timezone</span>
      <button
        type="button"
        className="timezone-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <span>
          <strong>{timezonePlace(value)}</strong>
          <small>
            {timezoneShortName(value, displayDate)} · {timezoneOffsetLabel(value, displayDate)} · {value}
          </small>
        </span>
        <ChevronDown size={14} />
      </button>
      <small className="timezone-origin">Your timezone: {slackTimezone}</small>
      {open && (
        <>
          <button
            type="button"
            className="timezone-backdrop"
            aria-label="Close timezone picker"
            onClick={() => {
              setOpen(false);
              setQuery("");
            }}
          />
          <div className="timezone-popover" role="dialog" aria-label="Choose event timezone">
            <div className="timezone-popover-head">
              <strong>Choose timezone</strong>
              <button type="button" aria-label="Close" onClick={() => setOpen(false)}>
                <X size={14} />
              </button>
            </div>
            <label className="timezone-search">
              <Search size={14} />
              <input
                ref={searchInput}
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setActiveIndex(0);
                }}
                placeholder="Search city, EST, CEST, UTC+2…"
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    setOpen(false);
                    setQuery("");
                  } else if (event.key === "ArrowDown") {
                    event.preventDefault();
                    setActiveIndex((current) => Math.min(current + 1, zones.length - 1));
                  } else if (event.key === "ArrowUp") {
                    event.preventDefault();
                    setActiveIndex((current) => Math.max(0, current - 1));
                  } else if (event.key === "Enter" && zones[activeIndex]) {
                    event.preventDefault();
                    choose(zones[activeIndex]);
                  }
                }}
              />
              {query && (
                <button type="button" aria-label="Clear search" onClick={() => setQuery("")}>
                  <X size={12} />
                </button>
              )}
            </label>
            <div className="timezone-results" role="listbox">
              {zones.map((timezone, index) => {
                const isSlack = timezone === slackTimezone;
                const isLocal = timezone === localTimezone;
                return (
                  <button
                    type="button"
                    role="option"
                    aria-selected={timezone === value}
                    className={index === activeIndex ? "active" : ""}
                    key={timezone}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => choose(timezone)}
                  >
                    <span className="timezone-offset">{timezoneOffsetLabel(timezone, displayDate)}</span>
                    <span className="timezone-result-copy">
                      <strong>{timezonePlace(timezone)}</strong>
                      <small>
                        {[
                          timezoneShortName(timezone, displayDate),
                          timezone,
                          isSlack ? "Slack" : "",
                          isLocal ? "Local" : "",
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </small>
                    </span>
                    {timezone === value && <Check size={14} />}
                  </button>
                );
              })}
              {!zones.length && <div className="timezone-empty">No timezone matches “{query}”</div>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export function UserGroupPicker({
  value,
  onChange,
}: {
  value: { id: string; name: string } | undefined;
  onChange: (group: { id: string; name: string } | undefined) => void;
}) {
  const [groups, setGroups] = useState<Array<{ id: string; handle: string; name: string; memberCount: number }>>([]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const searchInput = useRef<HTMLInputElement>(null);
  const [lookup, setLookup] = useState<{ id: string; status: "loading" | "missing" | "error" } | null>(null);
  const queryId = query.trim().toUpperCase();
  const needsLookup = /^S[A-Z0-9]{5,}$/.test(queryId) && !groups.some((group) => group.id === queryId);
  useEffect(() => {
    if (!needsLookup || !open) return;
    let active = true;
    setLookup({ id: queryId, status: "loading" });
    const timer = window.setTimeout(() => {
      api.usergroup(queryId).then(
        (group) => {
          if (!active) return;
          if (group) {
            setGroups((current) => [...current.filter((item) => item.id !== group.id), group]);
            setLookup(null);
          } else setLookup({ id: queryId, status: "missing" });
        },
        () => {
          if (active) setLookup({ id: queryId, status: "error" });
        },
      );
    }, 300);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [queryId, needsLookup, open]);
  useEffect(() => {
    let active = true;
    api
      .usergroups()
      .then((items) => {
        if (active)
          setGroups((current) => [...items, ...current.filter((group) => !items.some((item) => item.id === group.id))]);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    if (open) searchInput.current?.focus();
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open]);
  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return groups;
    return groups.filter(
      (group) =>
        group.handle.toLowerCase().includes(normalized) ||
        group.name.toLowerCase().includes(normalized) ||
        group.id.toLowerCase().includes(normalized),
    );
  }, [groups, query]);
  const select = (group: { id: string; handle: string; name: string }) => {
    onChange({ id: group.id, name: group.handle || group.name });
    setOpen(false);
    setQuery("");
  };
  const selected = value ? groups.find((group) => group.id === value.id) : undefined;
  const showSearch = !value || open;
  return (
    <div className="ping-group-picker" ref={wrapRef}>
      <span className="ping-group-picker-label">User group</span>
      {!showSearch ? (
        <div className="user-group-selected-row">
          <span>
            <strong>@{value.name}</strong>
            <small>
              {selected?.memberCount ?? 0} {selected?.memberCount === 1 ? "member" : "members"} · Slack user group
            </small>
          </span>
          <button type="button" onClick={() => setOpen(true)}>
            Change
          </button>
        </div>
      ) : (
        <>
          <div className="user-group-search">
            <Search size={15} />
            <input
              ref={searchInput}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setActiveIndex(0);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={(event) => {
                if (event.key === "ArrowDown") {
                  event.preventDefault();
                  setActiveIndex((current) => Math.min(current + 1, filtered.length - 1));
                } else if (event.key === "ArrowUp") {
                  event.preventDefault();
                  setActiveIndex((current) => Math.max(0, current - 1));
                } else if (event.key === "Enter") {
                  const item = filtered[activeIndex];
                  if (item) {
                    event.preventDefault();
                    select(item);
                  }
                } else if (event.key === "Escape") {
                  event.preventDefault();
                  setOpen(false);
                  if (!value) setQuery("");
                }
              }}
              placeholder="Search by name, handle, or paste a group ID…"
              autoComplete="off"
              aria-label="Search user groups"
              aria-expanded={open}
              role="combobox"
            />
            {query && (
              <button type="button" aria-label="Clear search" onClick={() => setQuery("")}>
                <X size={12} />
              </button>
            )}
          </div>
          {open && (
            <div className="user-group-dropdown" role="listbox">
              {filtered.map((group, index) => (
                <button
                  type="button"
                  role="option"
                  aria-selected={activeIndex === index}
                  className={activeIndex === index ? "active" : ""}
                  key={group.id}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => select(group)}
                >
                  <span className="ping-group-result-copy">
                    <strong>@{group.handle}</strong>
                    <small>
                      {group.name} · {group.id} · {group.memberCount} {group.memberCount === 1 ? "member" : "members"}
                    </small>
                  </span>
                  {value?.id === group.id && <Check size={14} />}
                </button>
              ))}
              {!filtered.length && (
                <div className="user-group-empty" role="status">
                  {needsLookup && (lookup?.id !== queryId || lookup.status === "loading")
                    ? "Looking up Slack user group…"
                    : needsLookup && lookup?.status === "error"
                      ? "Couldn’t look up this group. Clear the search and try again."
                      : needsLookup
                        ? `No active Slack user group found for ${queryId}`
                        : `No user groups match “${query}”`}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function ActionsEditor({
  section,
  onChange,
  authorTimezone,
}: {
  section: Extract<MessageSection, { type: "actions" }>;
  onChange: (section: MessageSection) => void;
  authorTimezone: string;
}) {
  const [expandedId, setExpandedId] = useState(section.actions[0]?.id ?? "");
  // Slack is authoritative when it is available. The browser zone keeps the
  // RSVP template useful when an enterprise profile cannot be read by the bot.
  const [profileTimezone, setProfileTimezone] = useState("");
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const effectiveTimezone = profileTimezone || browserTimezone || authorTimezone || "UTC";
  useEffect(() => {
    let active = true;
    api
      .timezone()
      .then((profile) => {
        if (active) setProfileTimezone(profile.timezone);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);
  const updateAction = (index: number, patch: Partial<(typeof section.actions)[number]>) => {
    const actions = section.actions.map((action, current) => (current === index ? { ...action, ...patch } : action));
    onChange({ ...section, actions });
  };
  return (
    <div className="actions-editor">
      <div className="actions-heading">
        <span>
          <MousePointerClick size={15} /> Buttons
        </span>
        <span className="tracking-promise">
          <Check size={12} /> Every click is saved in Results
        </span>
      </div>
      {section.actions.map((action, index) => {
        const behavior = editorBehavior(action.behavior);
        const choice = actionChoices.find((item) => item.value === behavior) ?? actionChoices[0];
        const expanded = expandedId === action.id;
        const fields = formFieldsFor(action);
        const rsvp = action.rsvp ?? defaultRsvp(effectiveTimezone);
        const rsvpDstWarning = daylightSavingWarning(rsvp.eventStartsAt, rsvp.eventTimezone);
        const rsvpMessageTimeDisplay = rsvp.messageTimeDisplay ?? rsvp.timeDisplay;
        const updateRsvp = (patch: Partial<typeof rsvp>) => updateAction(index, { rsvp: { ...rsvp, ...patch } });
        return (
          <article className={`button-editor ${expanded ? "expanded" : ""}`} key={action.id}>
            <div className="button-editor-top">
              <button
                type="button"
                className="button-editor-toggle"
                aria-expanded={expanded}
                onClick={() => setExpandedId(expanded ? "" : action.id)}
              >
                <span className={`button-sample ${action.style}`}>{action.label || "Untitled button"}</span>
                <span className="button-editor-summary">
                  <strong>{choice.title}</strong>
                  <small>{choice.description}</small>
                </span>
                <ChevronDown size={15} />
              </button>
              <IconButton
                label="Remove button"
                disabled={section.actions.length === 1}
                onClick={() =>
                  onChange({ ...section, actions: section.actions.filter((_, current) => current !== index) })
                }
              >
                <Trash2 size={14} />
              </IconButton>
            </div>
            {expanded && (
              <div className="button-editor-body">
                <div className="button-appearance">
                  <div className="action-field">
                    <span>Button text</span>
                    <SlackField
                      mode="plain"
                      value={action.label}
                      maxLength={75}
                      onChange={(label) => updateAction(index, { label })}
                      ariaLabel="Button text"
                      placeholder="Button label"
                    />
                  </div>
                  <fieldset className="style-field">
                    <legend>Style</legend>
                    <div className="style-options">
                      {(["default", "primary", "danger"] as const).map((style) => (
                        <button
                          key={style}
                          type="button"
                          className={style}
                          aria-pressed={action.style === style}
                          onClick={() => updateAction(index, { style })}
                        >
                          {style === "default" ? "Standard" : style === "primary" ? "Green" : "Red"}
                        </button>
                      ))}
                    </div>
                  </fieldset>
                </div>

                <div className="action-picker-heading">
                  <strong>When someone clicks</strong>
                  <span>The click is recorded first</span>
                </div>
                <fieldset className="action-choice-grid">
                  <legend className="visually-hidden">Button action</legend>
                  {actionChoices.map((item) => {
                    const ActionIcon = item.icon;
                    return (
                      <button
                        key={item.value}
                        type="button"
                        aria-pressed={behavior === item.value}
                        className={behavior === item.value ? "selected" : ""}
                        onClick={async () => {
                          let timezone = effectiveTimezone;
                          if (item.value === "rsvp") {
                            const freshProfile = await api.timezone().catch(() => undefined);
                            if (freshProfile) {
                              timezone = freshProfile.timezone || browserTimezone || "UTC";
                              setProfileTimezone(freshProfile.timezone);
                            }
                          }
                          updateAction(index, behaviorPatch(action, item.value, timezone));
                        }}
                      >
                        <ActionIcon size={16} />
                        <span>
                          <strong>{item.title}</strong>
                          <small>{item.description}</small>
                        </span>
                        {behavior === item.value && <Check size={13} className="action-choice-check" />}
                      </button>
                    );
                  })}
                </fieldset>

                <div className="action-settings">
                  {behavior === "open_url" && (
                    <>
                      <label className="action-field action-field-wide">
                        Destination URL
                        <input
                          data-format-mode="none"
                          type="url"
                          value={action.url ?? ""}
                          onChange={(event) => updateAction(index, { url: event.target.value })}
                          placeholder="https://example.com"
                        />
                      </label>
                      <TemplateTextarea
                        id={`${action.id}-link-reply`}
                        label="Private click reply"
                        help="Shown only to the clicker while the link opens."
                        value={action.responseText ?? "Opening link…"}
                        placeholder="Opening link…"
                        onChange={(responseText) => updateAction(index, { responseText })}
                      />
                    </>
                  )}

                  {behavior === "open_form" && (
                    <>
                      <div className="form-meta-fields">
                        <div className="action-field">
                          <span>Form title</span>
                          <SlackField
                            mode="plain"
                            value={action.formTitle ?? "Quick form"}
                            maxLength={24}
                            onChange={(formTitle) => updateAction(index, { formTitle })}
                            ariaLabel="Form title"
                          />
                        </div>
                        <div className="action-field">
                          <span>Submit button</span>
                          <SlackField
                            mode="plain"
                            value={action.formSubmitLabel ?? "Submit"}
                            maxLength={24}
                            onChange={(formSubmitLabel) => updateAction(index, { formSubmitLabel })}
                            ariaLabel="Submit button"
                          />
                        </div>
                      </div>
                      <div className="form-builder">
                        <div className="form-builder-heading">
                          <strong>Questions</strong>
                          <span>{fields.length} / 5</span>
                        </div>
                        {fields.map((field, fieldIndex) => (
                          <div className="form-question" key={field.id}>
                            <div className="form-question-number">{fieldIndex + 1}</div>
                            <div className="form-question-fields">
                              <SlackField
                                mode="plain"
                                ariaLabel={`Question ${fieldIndex + 1}`}
                                value={field.label}
                                maxLength={200}
                                onChange={(label) =>
                                  updateAction(index, {
                                    behavior: "open_form",
                                    formFields: fields.map((item, current) =>
                                      current === fieldIndex ? { ...item, label } : item,
                                    ),
                                  })
                                }
                                placeholder="What do you want to ask?"
                              />
                              <div className="form-question-options">
                                <select
                                  aria-label={`Answer type for question ${fieldIndex + 1}`}
                                  value={field.type}
                                  onChange={(event) => {
                                    const type = event.target.value as FormField["type"];
                                    updateAction(index, {
                                      behavior: "open_form",
                                      formFields: fields.map((item, current) =>
                                        current === fieldIndex
                                          ? {
                                              ...item,
                                              type,
                                              options: isChoiceField(type)
                                                ? (item.options ?? ["Yes", "No"])
                                                : undefined,
                                            }
                                          : item,
                                      ),
                                    });
                                  }}
                                >
                                  <option value="short_text">Short answer</option>
                                  <option value="long_text">Long answer</option>
                                  <option value="single_select">Dropdown</option>
                                  <option value="radio">Radio buttons</option>
                                  <option value="checkboxes">Checkboxes</option>
                                  <option value="date">Date</option>
                                  <option value="user_select">Slack person</option>
                                  <option value="channel_select">Slack channel</option>
                                </select>
                                <label className="required-toggle">
                                  <input
                                    type="checkbox"
                                    checked={field.required}
                                    onChange={(event) =>
                                      updateAction(index, {
                                        behavior: "open_form",
                                        formFields: fields.map((item, current) =>
                                          current === fieldIndex ? { ...item, required: event.target.checked } : item,
                                        ),
                                      })
                                    }
                                  />
                                  Required
                                </label>
                                <IconButton
                                  label="Remove question"
                                  disabled={fields.length === 1}
                                  onClick={() =>
                                    updateAction(index, {
                                      behavior: "open_form",
                                      formFields: fields.filter((_, current) => current !== fieldIndex),
                                    })
                                  }
                                >
                                  <X size={13} />
                                </IconButton>
                              </div>
                              {isChoiceField(field.type) ? (
                                <SlackField
                                  mode={field.type === "radio" || field.type === "checkboxes" ? "mrkdwn" : "plain"}
                                  ariaLabel={`Choices for question ${fieldIndex + 1}`}
                                  className="choice-options"
                                  value={(field.options ?? ["Yes", "No"]).join("\n")}
                                  multiline
                                  maxLength={750}
                                  onChange={(value) =>
                                    updateAction(index, {
                                      behavior: "open_form",
                                      formFields: fields.map((item, current) =>
                                        current === fieldIndex
                                          ? {
                                              ...item,
                                              options: value
                                                .split(/\n+/)
                                                .map((s) => s.trim())
                                                .filter(Boolean)
                                                .slice(0, 10),
                                            }
                                          : item,
                                      ),
                                    })
                                  }
                                  placeholder="One choice per line"
                                />
                              ) : (
                                <SlackField
                                  mode="plain"
                                  ariaLabel={`Placeholder for question ${fieldIndex + 1}`}
                                  value={field.placeholder ?? ""}
                                  maxLength={150}
                                  onChange={(placeholder) =>
                                    updateAction(index, {
                                      behavior: "open_form",
                                      formFields: fields.map((item, current) =>
                                        current === fieldIndex
                                          ? { ...item, placeholder: placeholder || undefined }
                                          : item,
                                      ),
                                    })
                                  }
                                  placeholder={
                                    field.type === "date"
                                      ? "Choose a date"
                                      : field.type === "user_select"
                                        ? "Choose a person"
                                        : field.type === "channel_select"
                                          ? "Choose a channel"
                                          : "Placeholder text (optional)"
                                  }
                                />
                              )}
                            </div>
                          </div>
                        ))}
                        {fields.length < 5 && (
                          <button
                            type="button"
                            className="add-form-question"
                            onClick={() =>
                              updateAction(index, {
                                behavior: "open_form",
                                formFields: [
                                  ...fields,
                                  {
                                    id: `field_${newId().slice(0, 8)}`,
                                    label: "New question",
                                    type: "short_text",
                                    required: true,
                                  },
                                ],
                              })
                            }
                          >
                            <Plus size={13} /> Add question
                          </button>
                        )}
                        <label className="form-notification-toggle">
                          <input
                            type="checkbox"
                            checked={action.notifyAuthorOnResponse ?? false}
                            onChange={(event) => updateAction(index, { notifyAuthorOnResponse: event.target.checked })}
                          />
                          <span>
                            <strong>DM me every new response</strong>
                            <small>Receive the respondent and all submitted answers in Slack.</small>
                          </span>
                        </label>
                      </div>
                    </>
                  )}

                  {behavior === "show_ephemeral" && (
                    <TemplateTextarea
                      id={`${action.id}-ephemeral`}
                      label="Private message to the clicker"
                      help="Appears in the channel, but only that person can see it."
                      value={action.responseText ?? "Thanks — your response was recorded."}
                      placeholder="Write the private message"
                      onChange={(responseText) => updateAction(index, { responseText })}
                    />
                  )}

                  {behavior === "notify_sender" && (
                    <>
                      <TemplateTextarea
                        id={`${action.id}-author-message`}
                        label="DM sent to you"
                        help="bChannel sends this directly to you."
                        value={
                          action.notificationText ?? "{{clicker_mention}} clicked “{{button_label}}” in {{channel}}."
                        }
                        placeholder="Write the notification you receive"
                        onChange={(notificationText) => updateAction(index, { notificationText })}
                      />
                      <TemplateTextarea
                        id={`${action.id}-author-reply`}
                        label="Private confirmation to the clicker"
                        help="Lets them know their click reached you."
                        value={action.responseText ?? "Your message was sent."}
                        placeholder="Your message was sent."
                        onChange={(responseText) => updateAction(index, { responseText })}
                      />
                    </>
                  )}

                  {behavior === "dm_clicker" && (
                    <>
                      <TemplateTextarea
                        id={`${action.id}-clicker-dm`}
                        label="DM sent to the clicker"
                        help="Sent by bchannel as a direct message."
                        value={action.dmText ?? "Hi {{clicker_name}} — thanks for responding."}
                        placeholder="Write the direct message"
                        onChange={(dmText) => updateAction(index, { dmText })}
                      />
                      <TemplateTextarea
                        id={`${action.id}-dm-reply`}
                        label="Private confirmation in the channel"
                        help="Only the clicker sees this after the DM is sent."
                        value={action.responseText ?? "Check your DMs."}
                        placeholder="Check your DMs."
                        onChange={(responseText) => updateAction(index, { responseText })}
                      />
                    </>
                  )}

                  {behavior === "rsvp" && (
                    <div className="rsvp-settings">
                      <div className="rsvp-event-heading">
                        <CalendarClock size={17} />
                        <span>
                          <strong>Event details</strong>
                          <small>Used for confirmations, DMs, and reminders.</small>
                        </span>
                      </div>
                      <div className="rsvp-message-display">
                        <label className="rsvp-message-toggle">
                          <input
                            type="checkbox"
                            checked={rsvp.showEventDetails}
                            onChange={(event) => updateRsvp({ showEventDetails: event.target.checked })}
                          />
                          <span>
                            <strong>Show event details above the button</strong>
                            <small>Turn this off when the message already explains the event.</small>
                          </span>
                        </label>
                        {rsvp.showEventDetails && (
                          <div className="rsvp-message-options">
                            <label>
                              <input
                                type="checkbox"
                                checked={rsvp.showEventTitle}
                                onChange={(event) => updateRsvp({ showEventTitle: event.target.checked })}
                              />
                              Event name
                            </label>
                            <label>
                              <input
                                type="checkbox"
                                checked={rsvp.showEventTime}
                                onChange={(event) => updateRsvp({ showEventTime: event.target.checked })}
                              />
                              Localized date and time
                            </label>
                            <label className={!rsvp.showEventTime ? "disabled" : undefined}>
                              <input
                                type="checkbox"
                                disabled={!rsvp.showEventTime}
                                checked={rsvpMessageTimeDisplay === "local_and_original"}
                                onChange={(event) =>
                                  updateRsvp({
                                    messageTimeDisplay: event.target.checked ? "local_and_original" : "local",
                                  })
                                }
                              />
                              Also show original timezone
                            </label>
                          </div>
                        )}
                        <label className="rsvp-message-toggle">
                          <input
                            type="checkbox"
                            checked={rsvp.showAttendees}
                            onChange={(event) => updateRsvp({ showAttendees: event.target.checked })}
                          />
                          <span>
                            <strong>Show who’s going</strong>
                            <small>Keep a short public attendee list below the event.</small>
                          </span>
                        </label>
                        <label className="rsvp-message-toggle">
                          <input
                            type="checkbox"
                            checked={rsvp.showCountdown}
                            onChange={(event) => updateRsvp({ showCountdown: event.target.checked })}
                          />
                          <span>
                            <strong>Show a live countdown</strong>
                            <small>One short line directly above the RSVP button.</small>
                          </span>
                        </label>
                      </div>
                      <div className="rsvp-event-grid">
                        <div className="action-field action-field-wide">
                          <span>Event name</span>
                          <SlackField
                            mode="plain"
                            value={rsvp.eventTitle}
                            maxLength={150}
                            onChange={(eventTitle) => updateRsvp({ eventTitle })}
                            ariaLabel="RSVP event name"
                            placeholder="M*A*S*H movie night"
                          />
                        </div>
                        <RsvpDateTimePicker
                          startsAt={rsvp.eventStartsAt}
                          timezone={rsvp.eventTimezone}
                          format={rsvp.timeFormat}
                          onChange={(eventStartsAt) => updateRsvp({ eventStartsAt })}
                          onFormatChange={(timeFormat) => updateRsvp({ timeFormat })}
                        />
                        <TimezonePicker
                          value={rsvp.eventTimezone}
                          slackTimezone={effectiveTimezone}
                          eventStartsAt={rsvp.eventStartsAt}
                          onChange={(eventTimezone) => {
                            const localValue = zonedInputValue(rsvp.eventStartsAt, rsvp.eventTimezone);
                            updateRsvp({
                              eventTimezone,
                              eventStartsAt: zonedInputToIso(localValue, eventTimezone),
                            });
                          }}
                        />
                      </div>
                      <div className="rsvp-time-check">
                        <span>Event timezone: {rsvp.eventTimezone}</span>
                        {rsvpDstWarning && <p>{rsvpDstWarning}</p>}
                      </div>
                      <fieldset className="rsvp-time-display">
                        <legend>What RSVP confirmations show</legend>
                        <label>
                          <input
                            type="radio"
                            name={`${action.id}-time-display`}
                            checked={rsvp.timeDisplay === "local_and_original"}
                            onChange={() => updateRsvp({ timeDisplay: "local_and_original" })}
                          />
                          <span>
                            <strong>Their local time + original</strong>
                            <small>Clearest for international events</small>
                          </span>
                        </label>
                        <label>
                          <input
                            type="radio"
                            name={`${action.id}-time-display`}
                            checked={rsvp.timeDisplay === "local"}
                            onChange={() => updateRsvp({ timeDisplay: "local" })}
                          />
                          <span>
                            <strong>Their local time only</strong>
                            <small>Shorter confirmations and reminders</small>
                          </span>
                        </label>
                      </fieldset>

                      <div className="rsvp-delivery-list">
                        <div className="rsvp-delivery">
                          <label>
                            <input
                              type="checkbox"
                              checked={rsvp.sendEphemeral}
                              onChange={(event) => updateRsvp({ sendEphemeral: event.target.checked })}
                            />
                            <span>
                              <strong>Private reply in the channel</strong>
                              <small>Only the person who RSVP’d sees it.</small>
                            </span>
                          </label>
                          {rsvp.sendEphemeral && (
                            <TemplateTextarea
                              id={`${action.id}-rsvp-ephemeral`}
                              label="Reply"
                              help="Formatting, emoji, mentions, and event variables are supported."
                              value={rsvp.ephemeralText}
                              placeholder="You’re on the list."
                              includeEventVariables
                              onChange={(ephemeralText) => updateRsvp({ ephemeralText })}
                            />
                          )}
                        </div>
                        <div className="rsvp-delivery">
                          <label>
                            <input
                              type="checkbox"
                              checked={rsvp.dmClicker}
                              onChange={(event) => updateRsvp({ dmClicker: event.target.checked })}
                            />
                            <span>
                              <strong>DM the person who RSVP’d</strong>
                              <small>Send a persistent confirmation from bChannel.</small>
                            </span>
                          </label>
                          {rsvp.dmClicker && (
                            <TemplateTextarea
                              id={`${action.id}-rsvp-dm`}
                              label="DM"
                              help="{{event_time}} uses their Slack profile timezone and can also include the original timezone."
                              value={rsvp.dmText}
                              placeholder="You’re confirmed."
                              includeEventVariables
                              onChange={(dmText) => updateRsvp({ dmText })}
                            />
                          )}
                        </div>
                        <div className="rsvp-delivery">
                          <label>
                            <input
                              type="checkbox"
                              checked={rsvp.notifyYou}
                              onChange={(event) => updateRsvp({ notifyYou: event.target.checked })}
                            />
                            <span>
                              <strong>DM YOU for every RSVP</strong>
                              <small>You’ll get the person, event, and localized time.</small>
                            </span>
                          </label>
                          {rsvp.notifyYou && (
                            <TemplateTextarea
                              id={`${action.id}-rsvp-you`}
                              label="DM sent to YOU"
                              help="Includes the responder and localized event time."
                              value={rsvp.notifyYouText}
                              placeholder="Someone RSVP’d."
                              includeEventVariables
                              onChange={(notifyYouText) => updateRsvp({ notifyYouText })}
                            />
                          )}
                        </div>
                      </div>

                      <div className="rsvp-reminders">
                        <div className="rsvp-reminders-heading">
                          <strong>DM reminders</strong>
                          <span>Each enabled reminder uses its own message.</span>
                        </div>
                        {rsvp.reminders.map((reminder, reminderIndex) => (
                          <div
                            className={`rsvp-reminder ${reminder.enabled ? "enabled" : ""}`}
                            key={reminder.offsetMinutes}
                          >
                            <label>
                              <input
                                type="checkbox"
                                checked={reminder.enabled}
                                onChange={(event) =>
                                  updateRsvp({
                                    reminders: rsvp.reminders.map((item, current) =>
                                      current === reminderIndex ? { ...item, enabled: event.target.checked } : item,
                                    ),
                                  })
                                }
                              />
                              <span>
                                {reminder.offsetMinutes === 0
                                  ? "When the event starts"
                                  : `${reminder.offsetMinutes} minutes before`}
                              </span>
                            </label>
                            {reminder.enabled && (
                              <TemplateTextarea
                                id={`${action.id}-reminder-${reminder.offsetMinutes}`}
                                label="Reminder message"
                                help="Sent as a DM; event time uses the recipient’s Slack profile timezone."
                                value={reminder.message}
                                placeholder="Your event starts soon."
                                includeEventVariables
                                extraVariables={[["Reminder timing", "{{reminder_offset}}"]]}
                                onChange={(message) =>
                                  updateRsvp({
                                    reminders: rsvp.reminders.map((item, current) =>
                                      current === reminderIndex ? { ...item, message } : item,
                                    ),
                                  })
                                }
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {behavior === "join_user_group" && (
                    <div className="ping-group-settings">
                      <UserGroupPicker
                        value={action.userGroup}
                        onChange={(userGroup) => updateAction(index, { userGroup: userGroup ?? undefined })}
                      />

                      <div className="rsvp-delivery-list">
                        <div className="rsvp-delivery">
                          <label>
                            <input
                              type="checkbox"
                              checked={action.privateReply ?? true}
                              onChange={(event) => updateAction(index, { privateReply: event.target.checked })}
                            />
                            <span>
                              <strong>Private reply in the channel</strong>
                              <small>Only the person who clicked sees it.</small>
                            </span>
                          </label>
                          {(action.privateReply ?? true) && (
                            <TemplateTextarea
                              id={`${action.id}-ping-group-reply`}
                              label="Reply"
                              help="Appears in the channel, but only that person can see it."
                              value={action.responseText ?? "You’re in for @{{group_name}}. :yay:"}
                              placeholder="Write the private confirmation"
                              extraVariables={userGroupTemplateVariables}
                              onChange={(responseText) => updateAction(index, { responseText })}
                            />
                          )}
                        </div>
                        <div className="rsvp-delivery">
                          <label>
                            <input
                              type="checkbox"
                              checked={action.dmClicker ?? false}
                              onChange={(event) => updateAction(index, { dmClicker: event.target.checked })}
                            />
                            <span>
                              <strong>DM the person who clicked</strong>
                              <small>A persistent confirmation from bChannel in their DMs.</small>
                            </span>
                          </label>
                          {(action.dmClicker ?? false) && (
                            <TemplateTextarea
                              id={`${action.id}-ping-group-dm`}
                              label="DM"
                              help="Sent by bchannel as a direct message."
                              value={action.dmText ?? "You’re in for @{{group_name}}. :yay:"}
                              placeholder="You’re on the list."
                              extraVariables={userGroupTemplateVariables}
                              onChange={(dmText) => updateAction(index, { dmText })}
                            />
                          )}
                        </div>
                        <div className="rsvp-delivery">
                          <label>
                            <input
                              type="checkbox"
                              checked={action.notifyAuthorOnResponse ?? false}
                              onChange={(event) =>
                                updateAction(index, { notifyAuthorOnResponse: event.target.checked })
                              }
                            />
                            <span>
                              <strong>DM you when someone joins</strong>
                              <small>You’ll get the person and the group’s new member count.</small>
                            </span>
                          </label>
                          {(action.notifyAuthorOnResponse ?? false) && (
                            <TemplateTextarea
                              id={`${action.id}-ping-group-you`}
                              label="DM sent to YOU"
                              help="Includes the person and the member count."
                              value={
                                action.notificationText ??
                                "{{clicker_mention}} joined the *@{{group_name}}* user group ({{member_count}} members)."
                              }
                              placeholder="Someone joined."
                              extraVariables={userGroupTemplateVariables}
                              onChange={(notificationText) => updateAction(index, { notificationText })}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="action-field confirmation-field">
                  <span>Ask before running this (optional)</span>
                  <SlackField
                    mode="mrkdwn"
                    value={action.confirmation ?? ""}
                    maxLength={200}
                    onChange={(confirmation) => updateAction(index, { confirmation: confirmation || undefined })}
                    ariaLabel="Confirmation text"
                    placeholder="Slack will show this confirmation first"
                  />
                </div>
              </div>
            )}
          </article>
        );
      })}
      {section.actions.length < 5 && (
        <button
          type="button"
          className="add-action"
          onClick={() => {
            const id = `action_${newId().slice(0, 8)}`;
            setExpandedId(id);
            onChange({
              ...section,
              actions: [
                ...section.actions,
                {
                  id,
                  label: "New button",
                  behavior: "open_url",
                  url: "https://example.com",
                  style: "default",
                },
              ],
            });
          }}
        >
          <Plus size={14} /> Add button
        </button>
      )}
    </div>
  );
}
